<script>
import SearchIcon from 'static/fonts/LineIcons/magnifier.svg';
import Input from '@/components/Input.vue';
import ProductAddOn from '@/components/ProductAddOn.vue';
import Button from '@/components/Button.vue';

export default {
    components: {
        Button,
        ProductAddOn,
        Input,
        SearchIcon,
    },
    props: {
        product: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            recommended: true,
            categories: [],
            selectedAddOns: [],
            searchQuery: '',
            isLoading: false,
        };
    },
    computed: {
        filteredCategories() {
            return this.categories.map((category) => ({
                ...category,
                add_ons: category.add_ons.filter((addOn) => {
                    if (this.recommended && this.product.add_ons_count > 0) {
                        if (!addOn.product_ids.includes(this.product.id)) {
                            return false;
                        }
                    }

                    if (this.searchQuery) {
                        // match all terms separated by a space
                        const terms = this.searchQuery.split(' ');

                        if (!terms.every((term) => addOn.name.toLowerCase().includes(term.toLowerCase()))) {
                            return false;
                        }
                    }

                    return true;
                }),
            }))
                .filter((category) => category.add_ons.length);
        },
    },
    mounted() {
        this.$axios.get('/add-ons', {
            params: {
                product_id: this.product.id,
                is_in_stock: 1,
                is_active: 1,
            },
        })
            .then((response) => {
                this.categories = response.data;
            })
            .catch((error) => {
                console.error(error);
            });
    },
    methods: {
        selectAddOn(addOnId, quantity = 1) {
            this.isLoading = true;

            try {
                this.$axios.post(`/basket/${this.$store.getters['basket/id']}/add-ons`, {
                    product_id: this.product.id,
                    add_on_id: addOnId,
                    quantity,
                });

                this.selectedAddOns = this.selectedAddOns.filter((selected) => selected.id !== addOnId);

                if (quantity > 0) {
                    this.selectedAddOns.push({
                        id: addOnId,
                        quantity,
                    });
                }
            } catch (error) {
                console.error(error);
            } finally {
                this.isLoading = false;
            }
        },
        isSelected(addOnId) {
            return this.selectedAddOns.filter((selected) => selected.id === addOnId).length > 0;
        },
    },
};
</script>

<template>
    <div>
        <div class="md:flex justify-between">
            <div
                v-if="product.add_ons_count > 0"
                class="flex gap-4 mb-4 items-center"
            >
                <button
                    class="button"
                    :class="recommended ? 'active' : ''"
                    @click="recommended = true"
                >
                    Recommended
                </button>
                <button
                    class="button"
                    :class="recommended ? '' : 'active'"
                    @click="recommended = false"
                >
                    View all
                </button>
            </div>
            <Input
                v-model="searchQuery"
                placeholder="Search for an add-on..."
                class="max-w-screen-xs"
            />
        </div>

        <div>
            <div
                v-if="filteredCategories.length === 0"
                class="rounded-lg bg-grey-lightest p-4 text-center"
                @click="recommended = false"
            >
                <div
                    v-if="recommended && searchQuery.length > 0"
                    class="flex items-center justify-center gap-4"
                    @click="recommended = false"
                >
                    No recommended add-ons found for '{{ searchQuery }}'
                    <Button
                        bg="grey"
                        @click="recommended = false"
                    >
                        Search all add-ons
                    </Button>
                </div>
                <div
                    v-else
                >
                    No results
                </div>
            </div>
            <div
                v-for="(category, index) in filteredCategories"
                :key="`category-${index}`"
                class="my-4"
            >
                <h4 class="uppercase font-semibold text-sm mb-2">
                    {{ category.name }}
                </h4>

                <div class="grid grid-cols-3 md:grid-cols-10 gap-4">
                    <ProductAddOn
                        v-for="addOn in category.add_ons"
                        :key="`addOn-${addOn.id}`"
                        :is-selected="isSelected(addOn.id)"
                        :add-on="addOn"
                        :is-loading="isLoading"
                        @selected="selectAddOn(addOn.id, $event)"
                    />
                </div>

            </div>
        </div>
    </div>
</template>

<style scoped lang="scss">
.button {
    @apply px-4 py-3 rounded-full transition-fast select-none flex text-center leading-tight font-heading;
    &.active {
        @apply bg-primary text-white;
    }
    &:hover {
        &:not(:disabled) {
            @apply opacity-75 shadow-md;
        }
    }
    &:disabled {
        @apply opacity-50 cursor-default;
    }
    @media (max-width: config("theme.screens.sm")) {
        @apply px-1;
        font-size: 90%;
        min-height: 2.5rem;
        height: auto;
    }
}
.input {
    @apply w-full h-full text-center text-primary font-semibold;
    &:focus {
        @apply outline-none;
    }
    &::-webkit-inner-spin-button,
    &::-webkit-outer-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }
}
.icon {
    @apply h-full;
}
</style>
